/*
 * Copyright (C) 2018 Frontier Developments PLC.
 */

import {Vue} from 'vue-property-decorator';
import {Translations} from 'vuex-i18n';
import {Validator} from 'vee-validate';
import store from '@/store';
import * as moment from 'moment';

/**
 * Api service class used to leverage ajax library in order to communicate with api.
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
export default class LanguageService {
    /**
     * Language map used to determine which browser languages map to which translation files.
     *
     * @type {Map}
     */
    public static dictionary: Map<string, string> = new Map([
        ['en', 'en'],
        ['en-us', 'en'],
        ['en-gb', 'en'],
        ['en-au', 'en'],
        ['en-ca', 'en'],
        ['de', 'de'],
        ['de-de', 'de'],
        ['fr', 'fr'],
        ['fr-fr', 'fr'],
        ['es', 'es'],
        ['es-es', 'es'],
        ['pt', 'pt_pt'],
        ['pt-br', 'pt_pt'],
        ['pt-pt', 'pt_pt'],
        ['ja', 'ja'],
        ['ja-jp', 'ja'],
        ['zh', 'zh_cn'],
        ['zh-zh', 'zh_cn'],
        ['zh-hans', 'zh_cn'],
        ['zh-cn', 'zh_cn'],
        ['zh-tw', 'zh_tw'],
        ['zh_cn', 'zh_cn'],
        ['zh_tw', 'zh_tw'],
        ['zh-hant', 'zh_tw'],
        ['ko', 'ko'],
        ['ko-ko', 'ko'],
    ]);

    /**
     * The set of supported languages.
     */
    public static supported: string[] = [...new Set(Array.from(LanguageService.dictionary.values()))];

    /**
     * Initialise the language.
     *
     * Note: differes from the update function in that it "requires" the language for asynchronous data load.
     *
     * @param language
     */
    public static initialiseLanguage(language: string): void {
        // load saved language selection
        const locale: string = LanguageService.findLanguage(language.toLowerCase());

        if (!Vue.i18n.localeExists(locale)) {
            try {
                Vue.i18n.add(locale, require(`@/i18n/${locale}.json`));
            } catch (exception) {
                // do nothing
            }
        }

        // configure moment
        moment.locale(locale);

        // set language
        Vue.i18n.set(locale);
    }

    /**
     * Update the language.
     *
     * Performs a dynamic import of the language data.
     *
     * @param language
     */
    public static async updateLanguage(language: string): Promise<void> {
        // load saved language selection
        const locale: string = LanguageService.findLanguage(language.toLowerCase());

        if (Vue.i18n.locale() === locale) {
            return;
        }

        // update language
        await store.dispatch('settingsModule/updateLanguage', locale);

        // dynamically load if language doesn't exist
        if (!Vue.i18n.localeExists(locale)) {
            try {
                const translationModule: any = await import(`@/i18n/${locale}.json`);
                const translation: Translations = {};
                Object.assign(translation, translationModule);
                Vue.i18n.add(locale, translation);
                Vue.i18n.set(locale);
            } catch (exception) {
                // do nothing
            }
        }

        // configure moment
        moment.locale(locale);

        if (Vue.i18n.localeExists(locale)) {
            Vue.i18n.set(locale);
        }

        // configure vee-validate
        const lang: any = await import(`vee-validate/dist/locale/${language}`).catch(() => {
            return import(`vee-validate/dist/locale/en`);
        });

        Vue.nextTick(() => {
            Validator.localize(language, lang);
        });

        // update document title
        document.title = Vue.i18n.translate('app.title', {}) || 'Thememaker\'s Toolkit';
    }

    /**
     * Find the language from a given locale.
     *
     * @param locale
     */
    public static findLanguage(locale: string): string {
        return LanguageService.dictionary.get(locale) ||
        LanguageService.dictionary.get(locale.replace(/[_-].*/, '')) ||
        'en';
    }

}
